/**
 * 大报告导出：PDF / Word。
 *
 * 思路（前端浏览器导出，所见即所得）：
 *  - 报告正文是已渲染好的 DOM（含 Recharts 图表 SVG），传入该容器元素。
 *  - PDF：用 html2canvas 把容器整体截成高清位图，再用 jsPDF 按 A4 分页贴图。
 *  - Word：把容器内每个图表卡(SVG+HTML图例)整体 html2canvas 成 PNG，替换进 HTML 克隆，
 *         再包成 Word 可识别的 HTML(.doc)。Word 打开后保留文字排版 + 图表图片。
 *
 * 之所以 Word 走「图表转 PNG + HTML 包装(.doc)」而非 html-to-docx：后者依赖 Node Buffer，
 * 浏览器端不稳定；.doc(HTML) 方案零依赖、Word/WPS 均可打开、图表以图片完整保留。
 */
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

/** A4 纵向几何。 */
const A4_MARGIN_MM = 14;
/**
 * 位图渲染倍率。2→3：182mm 页宽下有效分辨率约 335dpi(原 223dpi)，
 * 中文正文 200% 缩放不糊、300% 明显改善。再高(4)对体积和 canvas 上限压力大，收益递减。
 */
const RENDER_SCALE = 3;

/**
 * 导出时把报告容器锁定到这个固定像素宽再截图。
 * 为什么固定宽：网页容器是 max-w-none，宽度随显示器变（宽屏可达 1500px+）。
 * 若按屏幕宽截图再缩到 A4 页宽，宽屏下会把整份报告缩得很小、文字如蚊、页面大片留白。
 * 锁到 ~800px（接近 A4 正文 CSS 宽）后截图，文字大小稳定、图表铺满页宽，跟显示器无关。
 */
const EXPORT_WIDTH_PX = 800;

/** 改宽后等图表(Recharts ResizeObserver)重排完成的固定时长。定值比"收敛判据"更稳，避免截到半重排。 */
const RELAYOUT_WAIT_MS = 600;

/** 保守的单 canvas 总像素上限（覆盖 Safari ~16.7M 的下限，留安全余量）。 */
const MAX_CANVAS_AREA = 16_000_000;

/**
 * 单边像素硬上限。Safari/Chrome 对 canvas 任一边有 ~16384px 上限，
 * 超过即静默返回空白/透明 canvas（比面积超限更隐蔽）。长报告在高 scale 下高度方向最易触顶，
 * 必须与面积上限一起约束 scale，否则长报告导出白页。留 384px 安全余量。
 */
const MAX_CANVAS_SIDE = 16_000;

/** 连等两帧：第一帧触发布局，第二帧确保 ResizeObserver 回调已 flush。 */
function nextFrame(): Promise<void> {
  return new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
  );
}

/** 固定延时（用于等 Recharts 重排稳定）。 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** 等待容器内所有 Recharts 图表完成初次布局（ResponsiveContainer 异步测量）。 */
async function waitForCharts(container: HTMLElement, timeout = 2500): Promise<void> {
  const start = Date.now();
  while (Date.now() - start < timeout) {
    const svgs = container.querySelectorAll('svg.recharts-surface');
    const ready =
      svgs.length > 0 &&
      Array.from(svgs).every((s) => (s as SVGSVGElement).getBoundingClientRect().width > 0);
    // 若页面没有图表（纯文字报告）也直接返回
    if (ready || container.querySelectorAll('.report-chart-container').length === 0) return;
    await new Promise((r) => setTimeout(r, 120));
  }
}

/**
 * 找断页点：从理想断点向上回扫，找一整行「近白」的像素行作为切口，
 * 且该行不得落入任何图表的垂直区间(forbidden)——避免把图表拦腰切开。
 * 找不到合格白行 → 硬切在理想断点（宁可切文字行间，也不切图）。
 */
function findWhitecutY(
  data: Uint8ClampedArray,
  canvasW: number,
  canvasH: number,
  startY: number,
  maxSlice: number,
  forbidden: Array<[number, number]>, // canvas 像素坐标的图表区间 [top,bottom]
): number {
  const idealEnd = Math.min(startY + maxSlice, canvasH);
  if (idealEnd >= canvasH) return canvasH - startY; // 最后一页
  const minEnd = Math.max(startY + Math.floor(maxSlice * 0.5), startY + 1);
  const step = Math.max(1, Math.floor(canvasW / 256));
  const inForbidden = (y: number) => forbidden.some(([t, b]) => y > t && y < b);
  for (let y = idealEnd - 1; y >= minEnd; y--) {
    if (inForbidden(y)) continue; // 不在图表内部切
    let white = true;
    const rowOffset = y * canvasW * 4;
    for (let x = 0; x < canvasW; x += step) {
      const i = rowOffset + x * 4;
      // 阈值 250：只把「几乎纯白」判为可切，避免 #f8fafc(248) 卡片/图表底被误判
      if (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250) {
        white = false;
        break;
      }
    }
    if (white) return y - startY;
  }
  return idealEnd - startY; // 无合格白行 → 硬切理想断点
}

/**
 * 导出 PDF —— live-relayout + single-canvas。根治三症状：
 *  症状1(图右侧被裁): 截图前把 live 图表宿主改到打印宽(≈688px)，触发真 Recharts 重排，
 *     图画在窄宽内、柱子/图例不出界、图内文字不缩水。
 *  症状2(标题/文字遮挡): 只做一次整容器 html2canvas，块间位置由浏览器布局引擎决定，无手工累加错位。
 *  症状3(不居中): 每页 drawW=usableW、x=margin，左右对称。
 * 另修：白行阈值 250 且禁止在图表垂直区间内断页(防切图)；canvas 面积超限自动降 scale(防 OOM/半白)。
 * @param container 报告正文 DOM 容器
 * @param filename  文件名（不含扩展名）
 */
export async function exportReportPdf(container: HTMLElement, filename: string): Promise<void> {
  await waitForCharts(container);

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageW = pdf.internal.pageSize.getWidth();  // 210
  const pageH = pdf.internal.pageSize.getHeight(); // 297
  const margin = A4_MARGIN_MM;
  const usableW = pageW - margin * 2;              // 182mm
  const usableH = pageH - margin * 2;              // 269mm

  // 口径脚注(.report-basis)只在网页上看，导出 PDF 时隐藏；截图后 finally 里恢复。
  const bases = Array.from(container.querySelectorAll('.report-basis')) as HTMLElement[];
  const savedBaseDisplay = bases.map((el) => el.style.display);
  bases.forEach((el) => { el.style.display = 'none'; });

  // 把容器锁到固定导出宽（跟显示器无关），文字大小稳定、图表铺满页宽、页面不留大白边。
  const savedInline = { width: container.style.width, maxWidth: container.style.maxWidth };

  try {
    container.style.width = `${EXPORT_WIDTH_PX}px`;
    container.style.maxWidth = `${EXPORT_WIDTH_PX}px`;
    // 图表(width:100%)会跟着缩到固定宽 → Recharts ResizeObserver 异步重排。
    // 用固定延时等重排彻底完成（比"收敛判据"稳，避免截到柱子没画全）。
    await nextFrame();
    await delay(RELAYOUT_WAIT_MS);
    await nextFrame();

    // 记录每个图表宿主相对容器顶部的 y 区间(CSS px)，供分页禁切用（别把图切两页）
    const containerTop = container.getBoundingClientRect().top;
    const chartRangesCss: Array<[number, number]> = Array.from(
      container.querySelectorAll('.report-chart-container'),
    ).map((el) => {
      const r = (el as HTMLElement).getBoundingClientRect();
      return [r.top - containerTop, r.bottom - containerTop] as [number, number];
    });

    // 面积 + 单边双重上限保护：估算 scale，超限则等比降 scale。
    // 关键：不仅约束总面积，还要约束高度单边(长报告在高 scale 下最易撞 16384px 而静默白页)。
    const cssW = Math.ceil(container.scrollWidth);
    const cssH = Math.ceil(container.scrollHeight);
    let scale = RENDER_SCALE;
    // 1) 面积约束
    if (cssW * scale * cssH * scale > MAX_CANVAS_AREA) {
      scale = Math.sqrt(MAX_CANVAS_AREA / (cssW * cssH));
    }
    // 2) 单边约束（宽、高分别不得超 MAX_CANVAS_SIDE）
    scale = Math.min(scale, MAX_CANVAS_SIDE / cssW, MAX_CANVAS_SIDE / cssH);
    // 3) 落地为两位小数，且不低于 1（真放不下时宁可退回低清也不白页）
    scale = Math.max(1, Math.floor(scale * 100) / 100);

    const canvas = await html2canvas(container, {
      scale,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: cssW,
      windowWidth: cssW,
      scrollX: 0,
      scrollY: 0,
    });
    if (canvas.width === 0 || canvas.height === 0) {
      throw new Error('报告截图失败（canvas 尺寸为 0，可能超出浏览器 canvas 面积上限）');
    }

    // 整幅等比缩放：canvas 宽 → usableW(mm)。每毫米对应像素、每页可容纳的像素高。
    const drawW = usableW;
    const pxPerMm = canvas.width / drawW;
    const pageSlicePx = Math.floor(usableH * pxPerMm);
    const cssToCanvas = canvas.height / cssH; // CSS px → canvas px
    const forbidden: Array<[number, number]> = chartRangesCss.map(
      ([t, b]) => [t * cssToCanvas, b * cssToCanvas] as [number, number],
    );

    let pixels: Uint8ClampedArray | null = null;
    try {
      const ctx = canvas.getContext('2d');
      if (ctx) pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    } catch {
      pixels = null; // tainted canvas → 降级硬切
    }

    let y = 0;
    let first = true;
    while (y < canvas.height) {
      const sliceH = pixels
        ? findWhitecutY(pixels, canvas.width, canvas.height, y, pageSlicePx, forbidden)
        : Math.min(pageSlicePx, canvas.height - y);
      const safeSliceH = Math.max(1, Math.min(sliceH, canvas.height - y)); // 防死循环

      const slice = document.createElement('canvas');
      slice.width = canvas.width;
      slice.height = safeSliceH;
      const sctx = slice.getContext('2d')!;
      sctx.fillStyle = '#ffffff';
      sctx.fillRect(0, 0, slice.width, slice.height);
      sctx.drawImage(canvas, 0, y, canvas.width, safeSliceH, 0, 0, canvas.width, safeSliceH);

      const sliceMmH = safeSliceH / pxPerMm;
      if (!first) pdf.addPage();
      pdf.addImage(slice.toDataURL('image/jpeg', 0.95), 'JPEG', margin, margin, drawW, sliceMmH);

      first = false;
      y += safeSliceH;
    }

    pdf.save(`${filename}.pdf`);
  } finally {
    bases.forEach((el, i) => { el.style.display = savedBaseDisplay[i]; }); // 恢复口径脚注
    container.style.width = savedInline.width;                              // 恢复容器宽
    container.style.maxWidth = savedInline.maxWidth;
    await nextFrame();
  }
}

/** 把正文 HTML 包成 Word 可识别的 .doc(HTML) Blob（A4 版式 + 中文字体 + 表格/图片分页规则）。 */
function buildWordDocBlob(title: string, bodyHtml: string): Blob {
  const html =
    '<html xmlns:o="urn:schemas-microsoft-com:office:office" ' +
    'xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">' +
    '<head><meta charset="utf-8"><title>' + title + '</title>' +
    // Word 页面版式：A4 纵向 + 2.5cm/2cm 页边距（正文可用宽约 16cm）
    '<!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View>' +
    '<w:Zoom>100</w:Zoom><w:DoNotOptimizeForBrowser/></w:WordDocument></xml><![endif]-->' +
    '<style>' +
    '@page{size:21cm 29.7cm;margin:2.5cm 2cm 2.5cm 2cm;}' +
    'div.WordSection1{page:WordSection1;}' +
    'body{font-family:"Microsoft YaHei","SimSun",sans-serif;font-size:12pt;line-height:1.6;color:#1f2937;}' +
    'h1{font-size:20pt;text-align:center;} h2{font-size:16pt;} h3{font-size:14pt;} h4{font-size:13pt;}' +
    'table{border-collapse:collapse;} td,th{border:1px solid #999;padding:4px 8px;}' +
    'tr{page-break-inside:avoid;}' + // 长表格跨页时不从行中间劈开
    'img{max-width:100%;page-break-inside:avoid;}' +
    '.report-chart-container{page-break-inside:avoid;}' +
    '</style></head><body><div class="WordSection1">' + bodyHtml + '</div></body></html>';
  return new Blob(['﻿', html], { type: 'application/msword;charset=utf-8' });
}

/**
 * 导出 Word：图表整卡(SVG+自定义图例) html2canvas 截成 PNG 内嵌，正文包成 .doc(HTML)。
 *
 * 之所以截"整个 .report-chart-container"而非只转 SVG：自定义图例(TierLegend)是 HTML div，
 * 不在 SVG 里；只转 SVG 时图例以裸 HTML 进 Word，色块/布局全丢(需求修改单第5条"图例显示不对")。
 *
 * 预警名单（不含 SVG 的纯表格卡）单独拆成一个 .doc：300+ 行名单若截成一张超长图，
 * Word 图片无法跨页、只显示一页高度(半截 bug)。有名单 → 打 zip(大报告 + 预警名单两个文件)；
 * 无名单(市级) → 保持直接下载单个 .doc。
 */
export async function exportReportWord(container: HTMLElement, filename: string): Promise<void> {
  await waitForCharts(container);
  // 克隆一份，把里面的图表卡片整体替换成 PNG <img>
  const clone = container.cloneNode(true) as HTMLElement;
  // 口径脚注(.report-basis)只在网页上看，导出 Word 时从克隆体里移除
  clone.querySelectorAll('.report-basis').forEach((el) => el.remove());
  const srcCards = Array.from(container.querySelectorAll('.report-chart-container')) as HTMLElement[];
  const cloneCards = Array.from(clone.querySelectorAll('.report-chart-container')) as HTMLElement[];
  // Word A4 正文可用宽约 600px；图按此宽度等比缩放，避免原始尺寸超页被截断
  const WORD_IMG_W = 600;
  const listName = `${filename}_预警名单`;
  let warningBodyHtml = '';

  for (let i = 0; i < srcCards.length; i++) {
    // 纯表格卡(无 SVG) = 预警名单：抽成独立 .doc 的正文，报告里替换为指引文字
    if (!srcCards[i].querySelector('svg.recharts-surface')) {
      const cc = cloneCards[i];
      if (!cc) continue;
      if (cc.querySelector('table')) {
        // 清掉卡片样式/横向滚动/最小宽，Word 原生表格自动跨页
        cc.className = '';
        cc.style.cssText = '';
        cc.querySelectorAll('div').forEach((d) => {
          const el = d as HTMLElement;
          if (el.style.overflowX) el.style.overflowX = 'visible';
        });
        const tbl = cc.querySelector('table') as HTMLElement;
        tbl.style.minWidth = '';
        tbl.style.width = '100%';
        warningBodyHtml += cc.innerHTML;
        const note = document.createElement('p');
        note.textContent = `预警名单详见压缩包内《${listName}.doc》文件。`;
        cc.replaceWith(note);
      }
      continue;
    }
    let url = '';
    let w = 0, h = 0;
    try {
      const canvas = await html2canvas(srcCards[i], {
        scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false,
      });
      if (canvas.width > 0 && canvas.height > 0) {
        url = canvas.toDataURL('image/png');
        w = canvas.width / 2; h = canvas.height / 2;
      }
    } catch { /* 截失败则跳过该图（保底不阻断导出） */ }
    if (!url) continue;
    const dispW = Math.min(WORD_IMG_W, w);
    const dispH = Math.round((h / w) * dispW);
    const img = document.createElement('img');
    img.src = url;
    img.width = dispW;
    img.height = dispH;
    img.style.cssText = `width:${dispW}px;height:${dispH}px;display:block;margin:8px auto;`;
    cloneCards[i]?.replaceWith(img);
  }

  const reportBlob = buildWordDocBlob(filename, clone.innerHTML);

  // 无预警名单(市级) → 保持原行为：直接下载单个 .doc
  if (!warningBodyHtml) {
    saveAs(reportBlob, `${filename}.doc`);
    return;
  }

  // 有预警名单(校级) → 名单单独成 .doc，与大报告一起打 zip
  const listBlob = buildWordDocBlob(listName,
    `<h2>调查预警名单</h2>${warningBodyHtml}`);
  const zip = new JSZip();
  zip.file(`${filename}.doc`, reportBlob);
  zip.file(`${listName}.doc`, listBlob);
  const zipBlob = await zip.generateAsync({ type: 'blob' });
  saveAs(zipBlob, `${filename}.zip`);
}
